import type { FocusEvent, FormEvent, KeyboardEvent, Ref } from 'react'
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useAsync } from 'react-use'
import { getCaretCoordinates } from 'src/utils/caretPosition'
import { useForkRef } from 'src/utils/hooks/useForkRef'
import type {
  CompletionItem,
  CompletionResultResponse,
} from 'src/workers/tagCompleteType'
import type {
  PromptsCompletionDisplayProps,
  PromptsCompletionDisplayRef,
} from './PromptsCompletionDisplay'
import { PromptsCompletionDisplay } from './PromptsCompletionDisplay'
import { CompletionCancelledError } from './PromptsCompletionProvider'
import { PromptsCompletionContext } from './PromptsCompletionProvider'

export interface PromptsCompletionTextareaProps {}

interface PromptsData {
  prompts: string
  selectionStart: number
  selectionEnd: number
}

type PromptsCompletionResult = CompletionResultResponse &
  PromptsData & {
    query: string
    prefix: string
    suffix: string
  }

export const PromptsCompletionTextarea = forwardRef(
  function PromptsCompletionTextarea(
    props: JSX.IntrinsicElements['textarea'] & PromptsCompletionTextareaProps,
    propRef: Ref<HTMLTextAreaElement>,
  ) {
    const { workerController } = useContext(PromptsCompletionContext)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const promptsCompletionDisplayRef =
      useRef<PromptsCompletionDisplayRef>(null)
    const ref = useForkRef(propRef, textareaRef)
    const enableCompletion = workerController != null

    const [completionPos, setCompletionPos] =
      useState<PromptsCompletionDisplayProps['pos']>()
    const [promptsData, setPromptsData] = useState<PromptsData>()
    const { value: completionResult } = useAsync(async () => {
      if (workerController && promptsData) {
        const { prompts, selectionStart, selectionEnd } = promptsData
        const before = prompts.slice(0, selectionStart)
        // search beforehand until any of , ( [ ] )
        const [, prefix = '', query = ''] =
          before.match(/(^|[\s\S]*[,()[\]]\s*)([^,()[\]]*)$/) ?? []
        // remove trailing comma
        const suffix = prompts.slice(selectionEnd).replace(/^\s*,\s*/, '')
        try {
          return {
            ...(await workerController.requestCompletion({
              query,
              prefix,
            })),
            ...promptsData,
            query,
            prefix,
            suffix,
          }
        } catch (e) {
          if (e instanceof CompletionCancelledError) return undefined
          throw e
        }
      }
    }, [
      workerController,
      promptsData?.prompts,
      promptsData?.selectionStart,
      promptsData?.selectionEnd,
    ])

    const [displayingCompletionResult, setDisplayingCompletionResult] =
      useState<PromptsCompletionResult | undefined>()
    const [selectedIndex, setSelectedIndex] = useState<number>(0)
    useEffect(() => {
      setDisplayingCompletionResult(completionResult)
      setSelectedIndex(0)
    }, [completionResult])
    const completionDisplayActive =
      (displayingCompletionResult?.completions?.length ?? 0) > 0

    const updateCompletionData = useCallback(
      (event: FormEvent) => {
        if (!enableCompletion || !textareaRef.current) return
        if ('key' in event && event.key === 'Escape') return
        const {
          value: prompts,
          selectionStart,
          selectionEnd,
        } = textareaRef.current

        const pos = getCaretCoordinates(textareaRef.current, selectionStart)
        const bounding = textareaRef.current.getBoundingClientRect()

        setCompletionPos({
          left: pos.left + bounding.x,
          top: pos.top + pos.height + bounding.y,
        })
        setPromptsData({
          prompts,
          selectionStart,
          selectionEnd,
        })
      },
      [setCompletionPos, setPromptsData, enableCompletion, textareaRef],
    )

    const applyCompletion = useCallback(
      async (toSelect: CompletionItem) => {
        if (!displayingCompletionResult || !toSelect || !textareaRef.current)
          return

        const { prefix, prompts, suffix } = displayingCompletionResult

        const setSelection = (start: number, end: number) => {
          textareaRef.current!.focus()
          textareaRef.current!.setSelectionRange(start, end)
        }

        // TODO: add back character & world support when we enable that feature again

        const completion = toSelect.label + ', '
        setSelection(prefix.length, prompts.length - suffix.length)
        document.execCommand('insertText', false, completion)
        setSelection(
          prefix.length + completion.length,
          prefix.length + completion.length,
        )

        setDisplayingCompletionResult(undefined)
      },
      [selectedIndex, displayingCompletionResult, textareaRef],
    )

    const handleBlur = useCallback(
      (e: FocusEvent) => {
        if (
          promptsCompletionDisplayRef.current?.containNode(
            e.relatedTarget as Node,
          )
        ) {
          return
        }
        setDisplayingCompletionResult(undefined)
      },
      [promptsCompletionDisplayRef],
    )

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (completionDisplayActive && displayingCompletionResult) {
          if (e.key === 'Tab' || e.key === 'Enter') {
            e.preventDefault()
            applyCompletion(
              displayingCompletionResult.completions[selectedIndex],
            )
          }
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault()
            const completionEntryCount =
              displayingCompletionResult.completions.length
            setSelectedIndex(
              selected =>
                (selected +
                  (e.key === 'ArrowUp' ? -1 : 1) +
                  completionEntryCount) %
                completionEntryCount,
            )
          }
        }
        if (e.key === 'Escape') {
          setDisplayingCompletionResult(undefined)
        }
      },
      [setDisplayingCompletionResult, selectedIndex, applyCompletion],
    )

    return (
      <>
        <textarea
          autoComplete="off"
          autoCorrect="off"
          data-form-type="other"
          {...props}
          ref={ref}
          onKeyDown={(...args) => {
            handleKeyDown(...args)
            props.onKeyDown?.(...args)
          }}
          onInput={(...args) => {
            updateCompletionData(...args)
            props.onInput?.(...args)
          }}
          onKeyUp={(...args) => {
            updateCompletionData(...args)
            props.onKeyUp?.(...args)
          }}
          onMouseUp={(...args) => {
            updateCompletionData(...args)
            props.onMouseUp?.(...args)
          }}
          onFocus={(...args) => {
            updateCompletionData(...args)
            props.onFocus?.(...args)
          }}
          onBlur={(...args) => {
            handleBlur(...args)
            props.onBlur?.(...args)
          }}
        />
        <PromptsCompletionDisplay
          ref={promptsCompletionDisplayRef}
          active={completionDisplayActive}
          pos={completionPos}
          selected={selectedIndex}
          items={displayingCompletionResult?.completions}
          command={applyCompletion}
          time={displayingCompletionResult?.time}
        />
      </>
    )
  },
)
